import { FC, useState, useEffect } from "react";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as ZapIcon } from "../../assets/zap.icon.svg";
import { ReactComponent as LayersIcon } from "../../assets/layers.icon.svg";
import { ReactComponent as BookIcon } from "../../assets/book.icon.svg";
import { ReactComponent as GraphIcon } from "../../assets/graph.icon.svg";
import { ReactComponent as TelescopeIcon } from "../../assets/telescope.icon.svg";
import { ReactComponent as GraduationHatIcon } from "../../assets/graduation-hat.icon.svg";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";

const QuestionBubble = ({
    icon,
    text,
    handleClick,
    className = ""
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    text: string;
    handleClick: (arg1: string) => void;
    className?: string;
}) => {
    return (
        <Button
            className={`question-bubble ${className}`}
            variant="outlined"
            onClick={() => handleClick(text)}
        >
            <SvgIcon className="question-bubble__icon" component={icon} inheritViewBox />
            <span className="question-bubble__text">{text}</span>
        </Button>
    );
};

export const ChatBackground = ({ handleQuestion }: { handleQuestion: (arg1: string) => void }) => {
    const { user, knowledgeBase, knowledgeBaseConfig } = FirebaseAuthContainer.useContainer();
    const [isAvailableContent, setIsAvailableContent] = useState<boolean>(false);
    const [isActiveKB, setIsActiveKB] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [sampleQuestion1, setSampleQuestion1] = useState<string>("");
    const [sampleQuestion2, setSampleQuestion2] = useState<string>("");
    const [sampleQuestion3, setSampleQuestion3] = useState<string>("");
    const [sampleQuestion4, setSampleQuestion4] = useState<string>("");
    const [sampleQuestion5, setSampleQuestion5] = useState<string>("");
    const [sampleQuestion6, setSampleQuestion6] = useState<string>("");

    useEffect(() => {
        setIsActiveKB(knowledgeBase?.status === "active");
        setIsAvailableContent(
            !!knowledgeBase && knowledgeBase.uid === user?.currentKnowledgeBaseId
        );
    }, [user, knowledgeBase]);

    useEffect(() => {
        if (!knowledgeBase || !isAvailableContent) return;

        setTitle(
            isActiveKB
                ? (knowledgeBaseConfig?.assistantTitle ?? knowledgeBase.displayName)
                : knowledgeBase.displayName
        );
        setDescription(
            isActiveKB
                ? (knowledgeBaseConfig?.assistantDescription ?? "")
                : "Sorry, but you can't use this knowledge base at the moment."
        );

        if (isActiveKB) {
            setSampleQuestion1(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 0
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[0]
                    : ""
            );
            setSampleQuestion2(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 1
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[1]
                    : ""
            );
            setSampleQuestion3(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 2
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[2]
                    : ""
            );
            setSampleQuestion4(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 3
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[3]
                    : ""
            );
            setSampleQuestion5(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 4
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[4]
                    : ""
            );
            setSampleQuestion6(
                knowledgeBaseConfig?.assistantSuggestedQuestions &&
                    knowledgeBaseConfig.assistantSuggestedQuestions.length > 5
                    ? knowledgeBaseConfig.assistantSuggestedQuestions[5]
                    : ""
            );
        }
    }, [knowledgeBase, knowledgeBaseConfig, isActiveKB, isAvailableContent]);

    const handleQuestionClick = (text: string) => {
        handleQuestion(text);
    };

    return (
        <div
            className={`chat-background ${isAvailableContent ? "chat-background--visible" : "chat-background--hidden"}`}
        >
            <div className="chat-background-header">
                {knowledgeBase?.logoUrl && (
                    <img className="chat-background-logo" src={knowledgeBase.logoUrl} alt="" />
                )}
                <div className="chat-background-info">
                    <span className="chat-background-title">{title}</span>
                    <span
                        className={`chat-background-description${!isActiveKB ? " chat-background-description--warning" : ""}`}
                    >
                        {description}
                    </span>
                    {!!user?.currentAgentUid && (
                        <span className="chat-background-all-assistants" role="note">
                            Use All Assistants is on — questions are routed to the assistant that
                            best covers them.
                        </span>
                    )}
                </div>
            </div>
            {isActiveKB && (
                <div className="chat-background-content">
                    {sampleQuestion1 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--medium"
                            icon={ZapIcon}
                            text={sampleQuestion1}
                            handleClick={handleQuestionClick}
                        />
                    )}
                    {sampleQuestion2 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--medium"
                            icon={LayersIcon}
                            text={sampleQuestion2}
                            handleClick={handleQuestionClick}
                        />
                    )}
                    {sampleQuestion3 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--large"
                            icon={BookIcon}
                            text={sampleQuestion3}
                            handleClick={handleQuestionClick}
                        />
                    )}
                    {sampleQuestion4 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--small chat-background-item--mobile-hidden"
                            icon={GraphIcon}
                            text={sampleQuestion4}
                            handleClick={handleQuestionClick}
                        />
                    )}
                    {sampleQuestion5 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--small"
                            icon={TelescopeIcon}
                            text={sampleQuestion5}
                            handleClick={handleQuestionClick}
                        />
                    )}
                    {sampleQuestion6 && (
                        <QuestionBubble
                            className="chat-background-item chat-background-item--small"
                            icon={GraduationHatIcon}
                            text={sampleQuestion6}
                            handleClick={handleQuestionClick}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
